import React, { useState, useEffect, useCallback, createContext, useContext, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Hammer, Clock, Wrench, LogOut, CreditCard, Coins, User, Settings, Server, FlaskConical, BarChart3, Shield, Code, Sun, Moon } from 'lucide-react';
import './App.css';

import BuildPage from './pages/BuildPage';
import LabsAdminPage from './pages/LabsAdminPage';
import LabsHomePage from './pages/LabsHomePage';
import HistoryPage from './pages/HistoryPage';
import ProjectDetail from './pages/ProjectDetail';
import MaintainProject from './pages/MaintainProject';
import MaintenanceDetail from './pages/MaintenanceDetail';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import BuyCreditsPage from './pages/BuyCreditsPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import SettingsPage from './pages/SettingsPage';
import { useAutoForceSso } from './lib/foundryAiSso';

// Global safety: catch ReferenceErrors BEFORE React — redirect to /history
if (typeof window !== 'undefined' && !window.__cfGlobalErrorInstalled) {
  window.__cfGlobalErrorInstalled = true;
  window.addEventListener('error', (event) => {
    if (event.error instanceof ReferenceError && event.error.message && event.error.message.includes('is not defined')) {
      event.preventDefault();
      if (window.location.pathname !== '/history') {
        window.location.replace('/history');
      }
      return false;
    }
  }, true);
}

// Error Boundary to catch component crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isStaleCache: false };
    this._redirecting = false;
  }
  static getDerivedStateFromError(error) {
    // ALL "X is not defined" ReferenceErrors = stale cached code
    if (error instanceof ReferenceError && error.message && error.message.includes('is not defined')) {
      return { hasError: true, error, isStaleCache: true };
    }
    return { hasError: true, error, isStaleCache: false };
  }
  componentDidCatch(error, errorInfo) {
    if (error instanceof ReferenceError && error.message && error.message.includes('is not defined')) {
      // Silently redirect to /history — never show error screen
      if (!this._redirecting) {
        this._redirecting = true;
        window.location.replace('/history');
      }
      return;
    }
    console.error('React crash:', error, errorInfo);
  }
  componentDidUpdate() {
    // Failsafe: if render fires before componentDidCatch redirect, redirect from here
    if (this.state.isStaleCache && !this._redirecting) {
      this._redirecting = true;
      window.location.replace('/history');
    }
  }
  render() {
    if (this.state.hasError) {
      if (this.state.isStaleCache) {
        // Render blank while redirect fires — user never sees error text
        return <div style={{ minHeight: '100vh' }} />;
      }
      return (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#ef4444', maxWidth: '500px', margin: '100px auto' }}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Something went wrong</h2>
          <p style={{ fontSize: '0.875rem', color: '#888', marginBottom: '1.5rem' }}>{this.state.error?.message || 'Unknown error'}</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button onClick={() => { this.setState({ hasError: false, error: null, isStaleCache: false }); }}
              style={{ padding: '0.5rem 1rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 600 }}>
              Try Again
            </button>
            <button onClick={() => { window.location.replace('/history'); }}
              style={{ padding: '0.5rem 1rem', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 600 }}>
              Back to History
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import DevOpsPage from './pages/DevOpsPage';
import TestingPage from './pages/TestingPage';
import ExecDashboardPage from './pages/ExecDashboardPage';
import GovernancePage from './pages/GovernancePage';
import PlaygroundPage from './pages/PlaygroundPage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem('cf-theme') || 'light'; } catch { return 'light'; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('cf-theme', theme); } catch {}
  }, [theme]);

  const toggleTheme = () => setThemeState(t => t === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        setCredits(userData.credits || 0);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshCredits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/credits`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits);
      }
    } catch {}
  };

  const logout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
    setCredits(0);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, credits, setCredits, refreshCredits, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login');
    }
  }, [loading, user, navigate, location.state]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return null;
  return children;
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user, credits, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  // Labs link + admin link are added for anyone whose email is on the
  // labs_whitelist (is_student) OR for a labs admin. Everyone else sees
  // the regular sidebar.
  const [labsAccess, setLabsAccess] = useState({ is_labs_access: false, is_admin: false });
  useEffect(() => {
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(info => { if (info) setLabsAccess(info); })
      .catch(() => {});
  }, []);
  return (
    <aside className={`app-sidebar ${isOpen ? 'open' : ''}`} data-testid="app-sidebar">
      <div className="sidebar-brand">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <img src="/logo.png" alt="CodeFoundry" style={{ width: 32, height: 32, borderRadius: '0.375rem' }} />
          <div style={{ flex: 1 }}>
            <h1>Code<span style={{ color: '#818cf8' }}>Foundry</span><span style={{ fontWeight: 400, opacity: 0.4 }}>.sh</span></h1>
            <p>by FoundryAI Labs</p>
          </div>
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </div>
      <nav className="sidebar-nav">
        <NavLink to="/" end className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-build-nav-link">
          <Hammer className="icon" /> Build
        </NavLink>
        <NavLink to="/history" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-history-nav-link">
          <Clock className="icon" /> History
        </NavLink>
        <NavLink to="/maintain" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-maintain-nav-link">
          <Wrench className="icon" /> Maintain
        </NavLink>
        <NavLink to="/playground" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-playground-nav-link">
          <Code className="icon" /> Playground
        </NavLink>
        {(labsAccess.is_labs_access || labsAccess.is_admin) && (
          <NavLink to="/labs" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-labs-nav-link">
            <FlaskConical className="icon" /> Labs
          </NavLink>
        )}
        <NavLink to="/settings" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-settings-nav-link">
          <Settings className="icon" /> Settings
        </NavLink>
        <NavLink to="/devops" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-devops-nav-link">
          <Server className="icon" /> DevOps
        </NavLink>
        <NavLink to="/testing" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-testing-nav-link">
          <FlaskConical className="icon" /> Testing
        </NavLink>
        <NavLink to="/executive" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-executive-nav-link">
          <BarChart3 className="icon" /> Executive
        </NavLink>
        <NavLink to="/governance" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onClose} data-testid="sidebar-governance-nav-link">
          <Shield className="icon" /> Governance
        </NavLink>
      </nav>
      <div style={{ padding: '0.75rem', borderTop: '1px solid hsl(var(--sidebar-border))' }}>
        <div style={{ padding: '0.625rem 0.875rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
            <Coins size={14} style={{ color: '#818cf8' }} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'hsl(var(--sidebar-fg))' }} data-testid="credit-balance">{credits} credits</span>
          </div>
          <button className="btn" style={{ width: '100%', fontSize: '0.75rem', padding: '0.375rem', justifyContent: 'center', marginBottom: '0.375rem', background: 'hsl(var(--sidebar-hover))', color: 'hsl(var(--sidebar-fg))', border: '1px solid hsl(var(--sidebar-border))' }} data-testid="buy-credits-button"
            onClick={() => navigate('/buy-credits')}>
            <CreditCard size={12} /> Buy Credits
          </button>
          <button className="btn" style={{ width: '100%', fontSize: '0.6875rem', padding: '0.25rem', justifyContent: 'center', color: 'hsl(var(--sidebar-muted))', background: 'transparent' }} data-testid="add-credits-button"
            onClick={() => navigate('/pricing')}>
            View Plans
          </button>
        </div>
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.875rem' }}>
            {user.picture ? <img src={user.picture} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} /> : <User size={16} style={{ color: 'hsl(var(--sidebar-muted))' }} />}
            <span style={{ fontSize: '0.75rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'hsl(var(--sidebar-fg))' }}>{user.name || user.email}</span>
            <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--sidebar-muted))', padding: '0.25rem' }} data-testid="logout-button">
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

const CreditTopBar = () => {
  const { credits } = useAuth();
  const navigate = useNavigate();
  return (
    <div data-testid="credit-topbar" style={{
      position: 'fixed', top: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.5rem 1rem', background: 'hsl(var(--card))',
      borderBottom: '1px solid hsl(var(--border))',
      borderLeft: '1px solid hsl(var(--border))',
      borderRadius: '0 0 0 0.5rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <Coins size={14} style={{ color: '#818cf8' }} />
        <span data-testid="topbar-credit-balance" style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>
          {credits} credits
        </span>
      </div>
      <button
        data-testid="topbar-buy-credits-btn"
        onClick={() => navigate('/buy-credits')}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
          background: '#6366f1', color: '#ffffff', border: 'none',
          fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        <CreditCard size={12} /> Buy Credits
      </button>
    </div>
  );
};

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isProjectPage = location.pathname.startsWith('/project/');
  return (
    <ProtectedRoute>
      <div className="app-shell">
        {/* Mobile Top Bar — hidden on project pages */}
        {!isProjectPage && (
        <div className="mobile-topbar" data-testid="mobile-topbar">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <div className="mobile-topbar-brand">
            <img src="/logo.png" alt="" style={{ width: 24, height: 24, borderRadius: '0.25rem' }} />
            <span>Code<span style={{ color: '#818cf8' }}>Foundry</span></span>
          </div>
          <div style={{ width: 22 }} />
        </div>
        )}
        {/* Credit TopBar — visible on project pages where sidebar is collapsed */}
        {isProjectPage && <CreditTopBar />}
        {/* Sidebar Overlay */}
        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />}
        {!isProjectPage && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        {isProjectPage && sidebarOpen && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        <div className={isProjectPage ? 'app-main app-main-full' : 'app-main'}>
          <main className={isProjectPage ? 'app-content app-content-full' : 'app-content'} style={{ paddingTop: '1.5rem' }}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<BuildPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/project/:id" element={<ProjectDetail />} />
              <Route path="/maintain" element={<MaintainProject />} />
              <Route path="/maintenance/:id" element={<MaintenanceDetail />} />
              <Route path="/buy-credits" element={<BuyCreditsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/subscription/success" element={<PaymentSuccessPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/devops" element={<DevOpsPage />} />
              <Route path="/devops/:projectId" element={<DevOpsPage />} />
              <Route path="/testing" element={<TestingPage />} />
              <Route path="/testing/:projectId" element={<TestingPage />} />
              <Route path="/executive" element={<ExecDashboardPage />} />
              <Route path="/governance" element={<GovernancePage />} />
              <Route path="/playground" element={<PlaygroundPage />} />
              <Route path="/labs" element={<LabsHomePage />} />
              <Route path="/labs/admin" element={<LabsAdminPage />} />
            </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
};

function AppRouter() {
  const location = useLocation();

  // FoundryAI SSO — session_id can arrive as hash (#session_id=...) OR query (?session_id=...).
  // If present, short-circuit to AuthCallback which exchanges it for a session_token.
  // Must be evaluated BEFORE useAutoForceSso so we don't bounce back to the hub.
  const hasSsoSession =
    location.hash?.includes('session_id=') ||
    new URLSearchParams(location.search).has('session_id');

  // FoundryAI SSO: auto-bounce anonymous visitors on protected routes to the SSO Hub.
  useAutoForceSso({
    isAuthenticated: false,
    publicRoutes: ["/login", "/signup", "/forgot-password", "/reset-password",
                   "/pricing", "/about", "/contact", "/careers",
                   "/terms", "/privacy", "/auth/callback"],
  });

  if (hasSsoSession) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<AppLayout />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </div>
  );
}

export default App;
